import { ChangeDetectionStrategy, Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { StatementsFacade } from '@app/modules/statements/services/statements.facade';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { groupMin } from '@app/shared/validators/group-min';
import { BehaviorSubject } from 'rxjs';
import {
  BrandSettingsCustomizationOptions,
  FeeColumnCustomizationOptions,
  HeadlineCustomizationOptions,
  Property,
  ReservationColumnCustomizationOptions,
  Statement,
} from '@app/modules/statements/models/statements.models';

@Component({
  selector: 'sbnb-statement-detail-customize',
  templateUrl: './statement-detail-customize.component.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatementDetailCustomizeComponent implements OnInit {
  @Input() data: { propertyId: Property['id']; propertyName: Property['name']; statementId: Statement['id'] };
  @Output() closed = new EventEmitter<void>();
  public customizations$;

  private mutuallyExclusiveGuestNames() {
    return (control: UntypedFormGroup): ValidationErrors | null => {
      if (!control) return null;

      const guestFirstName = control.get('guest_first_name')?.value;
      const guestFullName = control.get('guest_full_name')?.value;

      return guestFirstName && guestFullName ? { mutuallyExclusive: true } : null;
    };
  }

  public customizationsForm = this.fb.group({
    headline: this.fb.group(
      {
        property_name: [true],
        address: [true],
      },
      {
        validators: [groupMin(1)],
      }
    ),
    brand_settings: this.fb.group(
      {
        brand_info: [true],
        personal_info: [true],
      },
      {
        validators: [groupMin(1)],
      }
    ),
    reservation_columns: this.fb.group(
      {
        reservation_code: [true],
        guest_first_name: [true],
        guest_full_name: [true],
      },
      {
        validators: [groupMin(1), this.mutuallyExclusiveGuestNames()],
      }
    ),
    fee_columns: this.fb.group({
      rental_commissions: [false],
      fees_sum: [false],
      host_service_fee: [false],
      cleaning_fee: [false],
      extra_guest_fee: [false],
      linen_fee: [false],
      management_fee: [false],
      uncategorized_revenue: [false],
      pet_fee: [false],
      resort_fee: [false],
      community_fee: [false],
      upsell: [false],
      flat_fees: [false],
    }),
  });
  public saving$ = new BehaviorSubject<boolean>(false);

  constructor(
    private statementsService: StatementsFacade,
    private fb: FormBuilder,
    private toast: ToastNotificationsService
  ) {}

  ngOnInit() {
    this.customizations$ = this.statementsService.getCustomizeStatement(this.data.propertyId);
    this.customizations$.subscribe((customizations) => {
      const headlineCustomization = customizations.find((c) => c.key === 'headline');
      if (headlineCustomization?.values?.length) {
        const headlineValues = headlineCustomization.values as HeadlineCustomizationOptions[];
        this.customizationsForm.get('headline')?.patchValue({
          property_name: headlineValues.includes(HeadlineCustomizationOptions.PropertyName),
          address: headlineValues.includes(HeadlineCustomizationOptions.Address),
        });
      }

      const brandSettingsCustomization = customizations.find((c) => c.key === 'brand_settings');
      if (brandSettingsCustomization?.values?.length) {
        const brandSettingsValues = brandSettingsCustomization.values as BrandSettingsCustomizationOptions[];
        this.customizationsForm.get('brand_settings')?.patchValue({
          brand_info: brandSettingsValues.includes(BrandSettingsCustomizationOptions.BrandInfo),
          personal_info: brandSettingsValues.includes(BrandSettingsCustomizationOptions.PersonalInfo),
        });
      }

      const reservationColumnsCustomization = customizations.find((c) => c.key === 'reservation_columns');
      if (reservationColumnsCustomization?.values?.length) {
        const reservationColumnsValues =
          reservationColumnsCustomization.values as ReservationColumnCustomizationOptions[];
        this.customizationsForm.get('reservation_columns')?.patchValue({
          reservation_code: reservationColumnsValues.includes(ReservationColumnCustomizationOptions.ReservationCode),
          guest_first_name: reservationColumnsValues.includes(ReservationColumnCustomizationOptions.GuestFirstName),
          guest_full_name: reservationColumnsValues.includes(ReservationColumnCustomizationOptions.GuestFullName),
        });
      }

      const feeColumnsCustomization = customizations.find((c) => c.key === 'fee_columns');
      if (feeColumnsCustomization) {
        const feeColumnsValues = feeColumnsCustomization.values as FeeColumnCustomizationOptions[];
        this.customizationsForm.get('fee_columns')?.patchValue({
          rental_commissions: feeColumnsValues.includes(FeeColumnCustomizationOptions.RentalCommissions),
          fees_sum: feeColumnsValues.includes(FeeColumnCustomizationOptions.FeesSum),
          host_service_fee: feeColumnsValues.includes(FeeColumnCustomizationOptions.HostServiceFee),
          cleaning_fee: feeColumnsValues.includes(FeeColumnCustomizationOptions.CleaningFee),
          extra_guest_fee: feeColumnsValues.includes(FeeColumnCustomizationOptions.ExtraGuestFee),
          linen_fee: feeColumnsValues.includes(FeeColumnCustomizationOptions.LinenFee),
          management_fee: feeColumnsValues.includes(FeeColumnCustomizationOptions.ManagementFee),
          uncategorized_revenue: feeColumnsValues.includes(FeeColumnCustomizationOptions.UncategorizedRevenue),
          pet_fee: feeColumnsValues.includes(FeeColumnCustomizationOptions.PetFee),
          resort_fee: feeColumnsValues.includes(FeeColumnCustomizationOptions.ResortFee),
          community_fee: feeColumnsValues.includes(FeeColumnCustomizationOptions.CommunityFee),
          upsell: feeColumnsValues.includes(FeeColumnCustomizationOptions.Upsell),
          flat_fees: feeColumnsValues.includes(FeeColumnCustomizationOptions.FlatFees),
        });
      }
    });
  }

  public saveCustomizations(): void {
    if (this.customizationsForm.invalid) return;

    this.saving$.next(true);

    const formValues = this.customizationsForm.value;
    const customizations = Object.entries(formValues).reduce((acc, [key, values]: [string, any]) => {
      return [
        ...acc,
        {
          key,
          values: Object.entries(values)
            .filter(([, value]) => Boolean(value))
            .map(([k]) => k),
        },
      ];
    }, []);

    this.statementsService
      .customizeStatement(this.data.propertyId, Number(this.data.statementId), { customizations })
      .subscribe({
        next: () => {
          this.toast.success('Customizations saved!');
          this.closed.emit();
        },
        error: (error) => {
          this.toast.error('Error saving customizations');
        },
        complete: () => {
          this.saving$.next(false);
        },
      });
  }
}
